import { createUseStyles } from 'react-jss';
import { IonModal } from '@ionic/react';
import { PredefinedColors } from '@ionic/core';
import { isPlatform } from '../utils/isPlatform';
import { useBreakpoints } from '../theme/useBreakpoints';
import { IThemeColor } from '../theme/IThemeColor';
import { ModalHeader } from './ModalHeader';

type IStyleKeys = 'modal' | 'fullScreen';
interface IStyleProps { desktopMaxHeight?: number; desktopMinHeight?: number; desktopMaxWidth?: number; isAboveMd: boolean; };

const useStyles = createUseStyles<IStyleKeys, IStyleProps>({
    modal: {
        '--max-width': ({ desktopMaxWidth, isAboveMd }) => (isPlatform('desktop') || isAboveMd) && desktopMaxWidth ? `${desktopMaxWidth}px` : undefined,
        '--max-height': ({ desktopMaxHeight, isAboveMd }) => (isPlatform('desktop') || isAboveMd) && desktopMaxHeight ? `${desktopMaxHeight}px` : undefined,
        '--min-height': ({ desktopMinHeight, isAboveMd }) => (isPlatform('desktop') || isAboveMd) && desktopMinHeight ? `${desktopMinHeight}px` : undefined,

        // Bump Ionic's default centered-card height (600px) to 700px, at the same breakpoint where
        // Ionic switches to that presentation. Scoped to this shared Modal so it never overrides
        // modals that size themselves (bottom sheets, full-screen overlays) -- a global ion-modal
        // rule did exactly that and clipped the Sharing & Access sheet's footer.
        '@media (min-width: 768px) and (min-height: 768px)': {
            '--height': '700px'
        }
    },

    // Used instead of modal: the whole window at every width, where Ionic would draw a centered card
    // from 768px up. A class on the element outranks Ionic's :host rules, media query or not.
    fullScreen: {
        '--width': '100%',
        '--height': '100%',
        '--border-radius': '0',
        '--box-shadow': 'none',
        '--ion-safe-area-top': 'env(safe-area-inset-top)',
        '--ion-safe-area-bottom': 'env(safe-area-inset-bottom)',
        '--ion-safe-area-left': 'env(safe-area-inset-left)',
        '--ion-safe-area-right': 'env(safe-area-inset-right)'
    }
});

type IModalProps = {
    'data-id'?: string;
    'data-type'?: string;
    title: string;
    children: React.ReactNode;
    isOpen: boolean;
    backHandler?: () => void;
    closeHandler: () => void;
    minimizeHandler?: () => void;
    onWillPresent?: () => void;
    onDidPresent?: () => void;
    stackedModal?: boolean;
    keepContentsMounted?: boolean;
    desktopMaxWidth?: number;
    desktopMaxHeight?: number;
    desktopMinHeight?: number;
    maximizeDesktopHeight?: boolean;
    // Covers the whole window at every width; the desktop sizes are ignored.
    fullScreen?: boolean;
    excludeAutoDismiss?: boolean;
    preventBackdropDismiss?: boolean;
    hideCloseButton?: boolean;
    centerTitle?: boolean;
    titleColor?: IThemeColor;
    buttonColor?: PredefinedColors;
};

export const Modal: React.FC<IModalProps> = ({
    'data-id': dataId,
    'data-type': dataType,
    title,
    children,
    isOpen = false,
    backHandler,
    closeHandler,
    minimizeHandler,
    onWillPresent = () => {},
    onDidPresent = () => {},
    stackedModal = false,
    keepContentsMounted,
    desktopMaxWidth,
    desktopMaxHeight,
    desktopMinHeight,
    maximizeDesktopHeight = false,
    fullScreen = false,
    preventBackdropDismiss = false,
    hideCloseButton = false,
    centerTitle,
    titleColor,
    buttonColor,
    ...rest
}) => {
    if ((desktopMinHeight && desktopMinHeight > 590) || maximizeDesktopHeight) {
        if (!maximizeDesktopHeight) {
            console.warn('Modal desktop heights cannot be greater than 590');
        }
        desktopMinHeight = 590;
    }

    const { isAboveBreakpoint } = useBreakpoints();
    const classes = useStyles({ desktopMaxHeight, desktopMinHeight, desktopMaxWidth, isAboveMd: isAboveBreakpoint('md') });

    return (
        <IonModal
            {...rest} // do it this way so excludeAutoDismiss gets added without IDE complaining
            data-id={dataId}
            data-type={dataType}
            isOpen={isOpen}
            className={fullScreen ? classes.fullScreen : classes.modal}
            backdropDismiss={!preventBackdropDismiss}
            keepContentsMounted={keepContentsMounted}
            onWillPresent={() => {
                onWillPresent();
            }}
            onDidPresent={onDidPresent}
            onWillDismiss={(e: any) => {
                // invoke closeHandler, but only if the user clicked on the backdrop (or hit the escape key)
                // (this avoids invoking handler twice when close button clicked, or modal closed by consumer)
                if (e.detail.role === 'backdrop') {
                    closeHandler();
                }
            }}
        >
            <ModalHeader
                title={title}
                backHandler={backHandler}
                closeHandler={closeHandler}
                minimizeHandler={minimizeHandler}
                hideCloseButton={hideCloseButton}
                centerTitle={centerTitle}
                titleColor={titleColor}
                buttonColor={buttonColor}
            />
            {children}
        </IonModal>
    );
};
